<template>
  <div class="d-flex flex-column">
    <PageLoader v-if="loading" message="Loading forms..." />

    <!-- Not the empty state: this job may have a required-to-close form on it. -->
    <div
      v-else-if="loadFailed"
      class="no-results-message"
      data-cy="cf-load-error"
    >
      <i class="fad fa-triangle-exclamation"></i>
      Could not load this job's forms.
      <button
        type="button"
        class="ml-2 text-blue-600 hover:underline"
        data-cy="cf-retry"
        @click="reload"
      >
        Try again
      </button>
    </div>

    <template v-else>
      <CustomFormSubmissionList
        v-if="!currentSubmission"
        :submissions="submissions"
        @attach="showAttachModal = true"
        @select="selectSubmission"
        @send="openSend"
        @archive="archive"
        @unarchive="unarchive"
      />
      <CustomFormSubmissionView
        v-else
        :submission="currentSubmission"
        :editing="editing"
        :focus-question-id="focusQuestionId"
        :submitting="submitting"
        @back="clearSubmission"
        @submit="submitSubmission"
        @send="sendCurrent"
        @archive="archiveCurrent"
        @unarchive="unarchiveCurrent"
        @update:editing="setEditing"
      >
        <!-- Always supplied; the view decides when to use it, keyed on its
             `editing` prop. Gating here instead would rely on slot presence,
             which is not reactive. -->
        <template #answer="{ question, answer }">
          <CustomFormAnswerInput
            :question="question"
            :answer="answer"
            :state="saver.stateFor(question.id)"
            :pending="saver.isOutstanding(question.id)"
            @update="(payload) => saveAnswer(question, payload)"
          />
        </template>
      </CustomFormSubmissionView>
    </template>

    <AttachCustomFormModal
      v-if="showAttachModal"
      :saving="attaching"
      :archived-template-ids="archivedTemplateIds"
      @hide-modal="showAttachModal = false"
      @submit="attach"
    />

    <!-- Keyed on the submission so reopening for a different form remounts it
         rather than reusing the first form's loaded recipients. -->
    <SendCustomFormModal
      v-if="sendingSubmission"
      :key="sendingSubmission.id"
      :submission="sendingSubmission"
      :customer-id="customerId"
      :address-id="addressId"
      @hide-modal="sendingSubmission = null"
    />

    <CustomFormAmendWarningModal
      v-if="showAmendWarning"
      :required-for-close="currentSubmission?.required_for_close"
      @hide-modal="showAmendWarning = false"
      @confirm="confirmAmend"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { EventBus } from "@/lib/event_bus";
import { getQueryVariables, updateQueryParam } from "@/lib/url.js";

import PageLoader from "@/components/PageLoader.vue";
import AttachCustomFormModal from "@/CustomForms/AttachCustomFormModal.vue";
import CustomFormAmendWarningModal from "@/CustomForms/CustomFormAmendWarningModal.vue";
import CustomFormAnswerInput from "@/CustomForms/CustomFormAnswerInput.vue";
import CustomFormSubmissionList from "@/CustomForms/CustomFormSubmissionList.vue";
import CustomFormSubmissionView from "@/CustomForms/CustomFormSubmissionView.vue";
import SendCustomFormModal from "@/CustomForms/SendCustomFormModal.vue";
import {
  type AnswerWriteResult,
  useAnswerSave
} from "@/CustomForms/useAnswerSave";
import type {
  CustomFormAnswer,
  CustomFormSubmission,
  CustomFormSubmissionQuestion
} from "@/typings/customForm";

const QUERY_KEY = "custom_form_submission_id";
const QUESTION_KEY = "custom_form_question_id";

const store = useStore();
const router = useRouter();

const submissions = ref<CustomFormSubmission[]>([]);
const currentSubmission = ref<CustomFormSubmission | null>(null);
// One-shot: read off a deep link, handed to the view to scroll to, then dropped
// from the URL so a reload or a Back does not re-fire the jump.
const focusQuestionId = ref<number | null>(null);
const loading = ref(true);
const loadFailed = ref(false);
const attaching = ref(false);
const submitting = ref(false);
const archiving = ref(false);
const showAttachModal = ref(false);
// The form the send modal is open for, and the modal's own open state — one ref,
// so the two cannot disagree about which form is being sent.
const sendingSubmission = ref<CustomFormSubmission | null>(null);

// A row click and the popstate handler both set currentSubmission from fetches
// that cannot be cancelled, so each applies its result only while still newest.
let selectionToken = 0;

const jobId = computed(() => store.state.job?.id);
// The send modal reads the job's own email addresses from the address contact
// endpoint, which is keyed by customer and service address rather than by job.
const customerId = computed(() => store.state.job?.customer?.id);
const addressId = computed(() => store.state.job?.address?.id);

// Templates this job already holds an ARCHIVED copy of. Not an exclusion — attaching
// a second copy is allowed — but attaching one yields an EMPTY form while the answers
// sit on the archived row the default list hides, so the picker warns rather than
// letting an admin lose work to a mistaken archive. Unarchive is what brings the
// answers back.
const archivedTemplateIds = computed(() =>
  submissions.value
    .filter((submission) => submission.archived_at !== null)
    .map((submission) => submission.custom_form_template_id)
);

// Always asks for archived rows; the list component hides them behind its own
// Active/All filter, so switching it needs no refetch — and archivedTemplateIds
// above depends on them being in the payload.
async function loadSubmissions() {
  submissions.value = await store.dispatch("fetchJobCustomFormSubmissions", {
    jobId: jobId.value,
    includeArchived: true
  });
}

function fetchSubmission(id: number): Promise<CustomFormSubmission> {
  return store.dispatch("fetchJobCustomFormSubmission", {
    jobId: jobId.value,
    id
  });
}

// The list rows omit the sections tree, so opening one has to fetch it.
async function selectSubmission(submission: CustomFormSubmission) {
  const token = ++selectionToken;
  loading.value = true;
  try {
    const full = await fetchSubmission(submission.id);
    if (token !== selectionToken) return;

    currentSubmission.value = full;
    focusQuestionId.value = null;
    updateQueryParam(QUERY_KEY, submission.id);
  } finally {
    // Unconditional: a superseded fetch skipping this would strand the loader.
    loading.value = false;
  }
}

async function clearSubmission() {
  await saver.flushPending();
  selectionToken += 1;
  currentSubmission.value = null;
  updateQueryParam(QUERY_KEY, null);
}

// For JobPage's beforeTabChange, so returning to the tab starts at the list.
function setCurrentSubmission(submission: CustomFormSubmission | null) {
  selectionToken += 1;
  currentSubmission.value = submission;
}

// Flush first: an answer still inside its debounce would otherwise land after
// the submit and be stranded on a finalized submission.
async function submitSubmission() {
  const open = currentSubmission.value;
  if (!open || submitting.value) return;

  submitting.value = true;
  try {
    await saver.flushPending();
    currentSubmission.value = await store.dispatch(
      "submitJobCustomFormSubmission",
      { jobId: jobId.value, id: open.id }
    );
    window.notify?.success("Checklist submitted");
    await refreshList();
  } finally {
    submitting.value = false;
  }
}

async function attach(templateId: number) {
  attaching.value = true;
  try {
    // Opened from the response's own tree, and the refresh comes after: a failed
    // refresh must not make a successful attach look like a failure.
    const created = await store.dispatch("attachCustomFormToJob", {
      jobId: jobId.value,
      customFormTemplateId: templateId
    });
    showAttachModal.value = false;
    selectionToken += 1;
    currentSubmission.value = created;
    focusQuestionId.value = null;
    updateQueryParam(QUERY_KEY, created.id);
    window.notify?.success("Form attached successfully!");
    await refreshList();
  } finally {
    attaching.value = false;
  }
}

// Deliberately unconfirmed, matching how the platform archives everywhere else —
// templates, time cards, addresses and crew members all dispatch straight through.
// Nothing is destroyed: answers, photos and the audit trail survive, and Restore
// brings the form back in the state it left, so there is no orphaned work to warn
// about. One click there, one click back.
const ARCHIVAL = {
  archive: {
    action: "archiveJobCustomFormSubmission",
    notice: "Form archived"
  },
  unarchive: {
    action: "unarchiveJobCustomFormSubmission",
    notice: "Form restored"
  }
} as const;

function archive(submission: CustomFormSubmission) {
  return setArchived(submission, "archive");
}

function unarchive(submission: CustomFormSubmission) {
  return setArchived(submission, "unarchive");
}

function openSend(submission: CustomFormSubmission) {
  sendingSubmission.value = submission;
}

// The detail view acts on whatever it is showing, so it emits without a payload.
function sendCurrent() {
  if (currentSubmission.value) openSend(currentSubmission.value);
}

function archiveCurrent() {
  if (currentSubmission.value) return archive(currentSubmission.value);
}

function unarchiveCurrent() {
  if (currentSubmission.value) return unarchive(currentSubmission.value);
}

// Closes the open form first: archiving from the detail view would otherwise leave
// the reader inside a form that is no longer on the job.
//
// Takes the direction rather than the store action so the toast cannot drift from
// the request — one lookup decides both.
async function setArchived(
  submission: CustomFormSubmission,
  direction: keyof typeof ARCHIVAL
) {
  if (archiving.value) return;

  const { action, notice } = ARCHIVAL[direction];
  archiving.value = true;
  try {
    await store.dispatch(action, { jobId: jobId.value, id: submission.id });
    if (currentSubmission.value?.id === submission.id) await clearSubmission();
    window.notify?.success(notice);
    await refreshList();
  } finally {
    archiving.value = false;
  }
}

// Swallowed: the attach already succeeded and the interceptor flashed the error,
// so a stale list is the lesser evil.
async function refreshList() {
  try {
    await loadSubmissions();
  } catch {
    // See above.
  }
}

// The open submission is a separate object from the list rows, so refreshing only
// the list left its status chip, X/Y and answers stale — which is the very thing
// the refresh-job listener exists to prevent.
async function refreshOpenSubmission() {
  const open = currentSubmission.value;
  if (!open) return;

  const token = ++selectionToken;
  try {
    const full = await fetchSubmission(open.id);
    if (token === selectionToken) currentSubmission.value = full;
  } catch {
    // Leave what is on screen: the interceptor flashed it, and a submission that
    // has since been archived would otherwise blank the view mid-read.
  }
}

async function refreshAll() {
  await refreshList();
  await refreshOpenSubmission();
}

// Off by default: reading is the common case, so inputs are opt-in.
const editing = ref(false);
const showAmendWarning = ref(false);

// Keyed on the id, not the object: applyWrite swaps currentSubmission on every
// save, so watching the object itself would drop the user out of edit mode
// mid-keystroke. Identity is what matters — edit mode must not carry from one
// form onto another, and popstate can swap them without a list click.
watch(
  () => currentSubmission.value?.id,
  () => {
    editing.value = false;
    showAmendWarning.value = false;
  }
);

// A form submitted elsewhere while this editor is open — a technician finishing
// it in the field — must stop accepting keystrokes, because the next one would
// unsubmit their submission with no warning ever shown. Re-entering goes back
// through the warning, which is the point of closing it here.
//
// Cancelling as well as hiding the inputs: an edit still inside its debounce is
// already queued, so hiding alone would let it land afterwards and unsubmit the
// form anyway — the exact case this guard exists for.
watch(
  () => currentSubmission.value?.status,
  (status) => {
    if (status !== "submitted") return;

    saver.cancelAll();
    editing.value = false;
  }
);

// Entering edit mode on a submitted form asks first: the next changed answer
// reopens it, and that is not recoverable by undoing the keystroke. Leaving edit
// mode is unchanged, and must not drop an edit still inside its debounce.
async function setEditing(value: boolean) {
  if (!value) {
    await saver.flushPending();
  } else if (currentSubmission.value?.status === "submitted") {
    showAmendWarning.value = true;
    return;
  }

  editing.value = value;
}

function confirmAmend() {
  showAmendWarning.value = false;
  editing.value = true;
}

const saver = useAnswerSave({
  write: ({ answerId, attributes }) =>
    store.dispatch("writeCustomFormAnswer", {
      jobId: jobId.value,
      submissionId: currentSubmission.value?.id,
      answerId,
      attributes
    }),
  identify: identifyAnswer,
  onSaved: applyWrite,
  onConflict: () => navigateBack()
});

// Read from the open submission at send time rather than from the row that was
// edited: the first write to an unanswered question mints the answer, and
// applyWrite lands its id and lock_version here. A follow-up composed before that
// returned would otherwise still look like a first write.
function identifyAnswer(questionId: number) {
  const answer = findAnswer(questionId);
  return {
    answerId: answer?.id ?? null,
    lockVersion: answer?.lock_version ?? null
  };
}

function findAnswer(questionId: number): CustomFormAnswer | null {
  const questions = (currentSubmission.value?.sections ?? []).flatMap(
    (section) => section.questions
  );
  return (
    questions.find((question) => question.id === questionId)?.answer ?? null
  );
}

function saveAnswer(
  question: CustomFormSubmissionQuestion,
  {
    attributes,
    immediate
  }: { attributes: Record<string, unknown>; immediate: boolean }
) {
  return saver.save(question.id, attributes, { immediate });
}

// Adopt the server's answer rather than the value that was typed: WriteAnswer
// re-snapshots field_label/field_type and can clear a selection a template edit
// stranded, and progress and status come back in the same envelope so the rail,
// the X/Y and the banner move together with it.
function applyWrite({ answer, progress, status }: AnswerWriteResult) {
  const submission = currentSubmission.value;
  if (!submission) return;

  // The list row renders its own status chip from a separate object, so a status
  // the write changed leaves it stale — the same reason the submit path refreshes.
  if (status !== submission.status) refreshList();

  currentSubmission.value = {
    ...submission,
    status,
    progress,
    sections: (submission.sections ?? []).map((section) => ({
      ...section,
      questions: section.questions.map((question) =>
        question.id === answer.custom_form_question_id
          ? { ...question, answer }
          : question
      )
    }))
  };
}

// Resolved by id rather than from the loaded rows: a deep link can name a
// submission the list does not have yet.
async function syncFromQuery() {
  // Bumped before the branch so a URL with no submission also invalidates a row
  // click still in flight, which would otherwise reopen the view afterwards.
  const token = ++selectionToken;
  const id = getQueryVariables()[QUERY_KEY];
  if (!id) {
    currentSubmission.value = null;
    return;
  }
  const full = await fetchSubmission(parseInt(String(id), 10));
  if (token !== selectionToken) return;

  currentSubmission.value = full;
  consumeFocusQuestion();
}

// Only once the submission is actually open: a question id is meaningless
// without the form it belongs to, and the view can only find the anchor after
// the tree it names has rendered.
function consumeFocusQuestion() {
  const raw = getQueryVariables()[QUESTION_KEY];
  if (!raw) return;

  focusQuestionId.value = parseInt(String(raw), 10) || null;
  replaceWithoutParam(QUESTION_KEY);
}

async function navigateBack() {
  try {
    await syncFromQuery();
  } catch {
    currentSubmission.value = null;
    replaceWithoutParam(QUERY_KEY);
  }
}

// router.replace directly, not updateQueryParam(..., true): that helper takes a
// `replace` flag but hands it to router.push as a second argument, which
// vue-router ignores — so the param would be pushed and Back would land on it
// again, act on it again, and push again. For the submission id that means a 404
// loop; for the question id it means the jump re-firing on every Back. This also
// runs from popstate, where that would be inescapable.
//
// With no router in scope, updateQueryParam's own non-router branch reaches
// updatePath, which does honour replace.
function replaceWithoutParam(key: string) {
  if (!router) {
    updateQueryParam(key, null, true);
    return;
  }

  const query = { ...router.currentRoute.value.query };
  delete query[key];
  router.replace({ path: window.location.pathname, query });
}

async function load() {
  loading.value = true;
  loadFailed.value = false;
  try {
    await loadSubmissions();
    await navigateBack();
  } catch {
    loadFailed.value = true;
  } finally {
    loading.value = false;
  }
}

function reload() {
  load();
}

// Follows the job's own refresh so an open tab does not serve a stale status. A
// submission finalized in the tech app has no push signal of its own — that
// needs a channel message this story does not add.
onMounted(() => {
  window.addEventListener("popstate", navigateBack);
  EventBus.$on("refresh-job", refreshAll);
  load();
});

onBeforeUnmount(() => {
  saver.flushPending();
  window.removeEventListener("popstate", navigateBack);
  EventBus.$off("refresh-job", refreshAll);
});

watch(jobId, (id, previous) => {
  if (id && previous && id !== previous) load();
});

defineExpose({ setCurrentSubmission, refresh: refreshList });
</script>
